import { useContext, useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { FlowForm, useFlowFormGlobal, FlowFormLeaveContext, get2DKeyHandler, getKeyHandler, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { ShortcutsContext, ShortcutGroup, ADD_FEED_SHORTCUT, ADD_FEED_KEYS } from '../components/ShortcutsOverlay';
import { READER_KEYS_USED_KEY } from './Main';
import { Spinner, FocusText } from 't-lib-ui/widgets';
import { RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../components/MainLayout';
import { requestStartCollapsed } from '../components/ThreeColumnLayout';
import PageHeader from '../components/PageHeader';
import NewPostsIcon from '../components/NewPostsIcon';
import PlusIcon from '../components/PlusIcon';
import FeedIcon from '../components/FeedIcon';
import ChevronRightIcon from '../components/ChevronRightIcon';
import DiscoverTile from '../components/DiscoverTile';
import CategoryTile from '../components/CategoryTile';
import '../components/AddFeedBrowse.css';
import FolderIcon from '../components/FolderIcon';
import FeedSearchBox from '../components/FeedSearchBox';
import MailIcon from '../components/MailIcon';
import AllReadIllustration from '../components/AllReadIllustration';
import RssBankruptcyCard, { bankruptcyConditionsMet } from '../components/RssBankruptcyCard';
import HeaderTools from '../components/HeaderTools';
import LegalFooter from '../components/LegalFooter';
import FeedQuotaCard from '../components/FeedQuotaCard';
import { usePreferences } from '../connector/preferences';
import { getLastActiveMs } from '../connector/lastActive';
import { getLastMarkReadMs } from '../connector/lastMarkRead';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import { AppState } from '../context/general';
import { getMode } from '../connector/auth';
import { useGetCatalogCategories, useGetDiscoverOnboarding, useGetHomeContent } from '../connector/api';
import { useEngineState } from '../utils/useEngineState';
import { useGridColumns } from '../utils/useGridColumns';
import FeedLoadError from '../components/FeedLoadError';
import { MAX_ANON_FEEDS } from '../connector/feed-engine';
import { cameFromShare } from '../connector/anon-onboarding';
import { formatLastPostTime, formatCategory, formatStatCount, nameColor } from '../utils/formatting';
import type { RestUserFeed } from '../schema-copy/api-schema';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import { saveHomeScroll, takeHomeScrollTarget, homeCategoryAnchor, setPreviewReturnPath, setFeedOpenedFromTile, setAddFeedFromHome, setTopicViaAllTopics, setFeedOpenedFromSearch } from './homeScroll';
import OfflineNotice from '../components/OfflineNotice';
import { useReadingOffline } from '../utils/useOfflineMode';
import { isNativeApp } from '../utils/platform';
import './HomePage.css';

const HOME_SHORTCUTS: ShortcutGroup[] = [
  { title: 'Home', shortcuts: [
    { keys: ['j'], description: 'Open first feed with new posts' },
    { keys: ['r'], description: 'Open Read later list' },
  ] },
  { title: 'Navigation', shortcuts: [ADD_FEED_SHORTCUT] },
];

// Single-letter code appended to UserPreferences.dismissed once the keyboard-tips card is closed.
const KBD_CARD_DISMISS = 'k';
// Likewise for the new-user welcome hero (the occasional "Welcome back" hero is only closed for the visit).
const HERO_DISMISS = 'w';

// Per-device list of collapsed Home sections (a view convenience, so not a synced preference).
const COLLAPSED_SECTIONS_KEY = 'home-collapsed-sections';
type HomeSection = 'new' | 'discover' | 'categories' | 'stats';

const loadCollapsedSections = (): Set<HomeSection> => {
  try { return new Set(JSON.parse(localStorage.getItem(COLLAPSED_SECTIONS_KEY) || '[]')); } catch { return new Set(); }
};

const KeyboardIcon: React.FC<{ size?: number }> = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <path d="M6 10h.01M9.5 10h.01M13 10h.01M16.5 10h.01M6 13.5h.01M16.5 13.5h.01" />
    <path d="M9.2 13.5h5.6" />
  </svg>
);

// Only greet returning users with "Welcome back" if they've been away
// this long - seeing it right after you just left is irritating.
const WELCOME_BACK_AFTER_MS = 4 * 24 * 60 * 60 * 1000;
// If the last visit was longer ago than this, an empty unread list means
// "nothing new arrived yet" rather than "you just read everything".
const CAUGHT_UP_STALE_MS = 3 * 60 * 60 * 1000;
// If the user marked something read this recently, an empty unread list plainly means
// "you just caught up" — so we drop the "nothing new arrived" preamble and shorten the
// headline to "All read." instead.
const JUST_CAUGHT_UP_MS = 3 * 60 * 1000;

const HomePage: React.FC = () => {
  useFlowFormGlobal();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const { feedEngine } = appState;
  // Anonymous (no-login) visitors get a welcome tailored to first-time, possibly-unfamiliar users:
  // no OPML import, a nudge to log in, an explanation of what Downread is, and legal links.
  const anon = getMode() == 'anon';
  // Visitors who arrived by following a feed from a public Share/Subscribe link didn't come to
  // Downread because they heard about it — they came for that one feed. Greet them with the basics
  // (and the limits of anon mode) instead of the generic pitch. The flag is set on the /add subscribe page.
  const [fromShare] = useState(() => anon && cameFromShare());

  // Navigate to a full layout page; on mobile keep the sidebar drawer closed
  // so it doesn't open in front of the destination page.
  const navigate = (path: string) => {
    // Going to a feed in narrow/mobile opens straight into read mode (sidebar hidden);
    // the setup destinations keep the normal layout.
    if (path == '/a/new' || path == '/a/starred' || path == '/a/read-later' || path.startsWith('/a/feed/') || path.startsWith('/a/new/'))
      requestStartCollapsed();
    goTo(routerCtx, path);
  };

  const [preferences, updatePreferences] = usePreferences();
  const readingOffline = useReadingOffline();
  // Global stats for the returning-user dashboard.
  const onboarding = useGetDiscoverOnboarding({ enabled: true });
  // Discover categories. Logged-in users learn the current date from the feeds response; anonymous
  // mode falls back to the latest published file.
  const homeContent = useGetHomeContent(feedEngine.getHomeDate());

  const [collapsedSections, setCollapsedSections] = useState(loadCollapsedSections);
  // Add Feed's topic pages lead back here only when entered from the Categories section below.
  useEffect(() => setAddFeedFromHome(false), []);
  const openTopics = (path: string) => {
    setAddFeedFromHome(true);
    setTopicViaAllTopics(false);
    navigate(path);
  };
  const toggleSection = (section: HomeSection) => setCollapsedSections(prev => {
    const next = new Set(prev);
    if (!next.delete(section)) next.add(section);
    try { localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(Array.from(next))); } catch { /* storage unavailable */ }
    return next;
  });

  // Live snapshot of the user's library, for the vivid "returning user"
  // dashboard. All data is already in memory in the feed engine - no extra
  // network or DB requests (this page loads on every visit).
  const [feeds, setFeeds] = useState<RestUserFeed[]>(() => feedEngine.getFeeds());
  const [folders, setFolders] = useState(() => feedEngine.getFolders());
  const [counts, setCounts] = useState(() => ({ unread: 0, trackedUnread: 0, untrackedUnread: false, starred: feedEngine.getStarredCount(), readLater: feedEngine.getReadLaterCount() }));
  useEffect(() => {
    const recompute = () => {
      const fs = feedEngine.getFeeds();
      setFeeds([...fs]);
      setFolders(feedEngine.getFolders());
      setCounts({
        unread: fs.reduce((s, f) => s + feedEngine.getUnreadCount(f.id), 0),
        trackedUnread: fs.reduce((s, f) => s + (feedEngine.getShowRead(f.id) == 'u' ? feedEngine.getUnreadCount(f.id) : 0), 0),
        untrackedUnread: fs.some(f => feedEngine.getShowRead(f.id) == 'a' && feedEngine.getUnreadCount(f.id) > 0),
        starred: feedEngine.getStarredCount(),
        readLater: feedEngine.getReadLaterCount(),
      });
    };
    const unF = feedEngine.onFeedsChange(recompute);
    const unU = feedEngine.onUnreadChange(recompute);
    const unS = feedEngine.onStarChange(recompute);
    recompute();
    return () => { unF(); unU(); unS(); };
  }, [feedEngine]);

  const unreadFeeds = useMemo(
    () => feeds
      .filter(f => feedEngine.getUnreadCount(f.id) > 0)
      // Unread-tracking ('u') feeds rank above no-tracking ('a') feeds; within each
      // group, most-recent first.
      .sort((a, b) => {
        const ra = feedEngine.getShowRead(a.id) == 'a' ? 1 : 0;
        const rb = feedEngine.getShowRead(b.id) == 'a' ? 1 : 0;
        if (ra != rb) return ra - rb;
        return (b.lastNewPostAtSemi ?? 0) - (a.lastNewPostAtSemi ?? 0);
      }),
    [feeds, feedEngine]
  );
  // Topics of the Categories section, linking into Add Feed - the same list as its "Browse by topic"
  // (and sharing its 2h localStorage cache). Only fetched while the section is expanded.
  // Also needed (with the subcategories) for the suggestions of the search bar.
  const [searchState, setSearchState] = useState<'closed' | 'open' | 'closing'>('closed');
  const categoriesQuery = useGetCatalogCategories({ enabled: !collapsedSections.has('categories') || searchState == 'open' });
  // 'chat' is reserved for future applications and not offered for browsing yet (as in AddFeed).
  const searchCategories = useMemo(
    () => (categoriesQuery.data?.categories ?? []).filter(cat => cat != 'chat'),
    [categoriesQuery.data]);
  const topics = useMemo(() => searchCategories.filter(cat => !cat.includes('/')), [searchCategories]);
  // Two full rows, like Add Feed's start page; the column count follows AddFeedBrowse.css.
  const [topicGridRef, topicCols] = useGridColumns(4);

  // How many "New posts available" cards to show: 8 fills the 1- and 2-column grids, while the
  // 3-column grid of wide screens (a CSS container query) shows 9. The column count is read
  // from the rendered grid, so the CSS stays the single source of the breakpoint.
  const [recentGridRef, recentCols] = useGridColumns(2);
  const recentLimit = recentCols == 3 ? 9 : 8;
  const recentFeeds = useMemo(() => unreadFeeds.slice(0, recentLimit), [unreadFeeds, recentLimit]);

  // Folder grouping of the unread list. A folder gets a card when any of its feeds has
  // unread posts; unfiled unread feeds keep their individual cards. The folder view is
  // only offered when at least one folder actually groups more than one unread feed
  // (otherwise it adds nothing over the feed list).
  type HomeCard =
    | { kind: 'feed'; feed: RestUserFeed; browseOnly: boolean; recency: number }
    | { kind: 'folder'; name: string; members: RestUserFeed[]; unread: number; browseOnly: boolean; recency: number };
  const homeCards = useMemo<HomeCard[]>(() => {
    const unreadById = new Map(unreadFeeds.map(f => [f.id, f]));
    const namedFolders = Object.entries(folders).filter(([name]) => name != '');
    const inFolder = new Set(namedFolders.flatMap(([, ids]) => ids));
    const cards: HomeCard[] = namedFolders
      .map(([name, ids]) => {
        const members = ids.map(id => unreadById.get(id)).filter((f): f is RestUserFeed => !!f);
        return {
          kind: 'folder' as const,
          name,
          members,
          unread: members.reduce((s, f) => s + feedEngine.getUnreadCount(f.id), 0),
          browseOnly: members.every(f => feedEngine.getShowRead(f.id) == 'a'),
          recency: members.reduce((m, f) => Math.max(m, f.lastNewPostAtSemi ?? 0), 0),
        };
      })
      .filter(c => c.members.length);
    cards.push(...unreadFeeds
      .filter(f => !inFolder.has(f.id))
      .map(f => ({ kind: 'feed' as const, feed: f, browseOnly: feedEngine.getShowRead(f.id) == 'a', recency: f.lastNewPostAtSemi ?? 0 })));
    // Same ranking as the feed list: unread-tracking entries above browse-only, then most-recent first.
    cards.sort((a, b) => (a.browseOnly ? 1 : 0) - (b.browseOnly ? 1 : 0) || b.recency - a.recency);
    return cards;
  }, [unreadFeeds, folders, feedEngine]);
  const folderViewAvailable = useMemo(
    () => homeCards.some(c => c.kind == 'folder' && c.members.length > 1),
    [homeCards]);
  const folderMode = folderViewAvailable && preferences.homeGrouping == 'folders';
  const recentCards = useMemo(() => homeCards.slice(0, recentLimit), [homeCards, recentLimit]);

  // The search bar, toggled by the header's search button, slides in at the top of the page. It works
  // like Add Feed's search box, which shows the results (/a/add?q=<term>).
  const [searchInput, setSearchInput] = useState('');
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const toggleSearch = () => {
    if (searchState == 'open') return closeSearch();
    setSearchState('open');
    // The bar appears at the top; focusing its input (autoFocus) also closes any open menu or popup.
    pageRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const closeSearch = () => {
    setSearchState('closing');
    setSearchInput('');
    searchButtonRef.current?.focus();
  };
  const submitSearch = () => {
    const term = searchInput.trim();
    if (term) navigate(`/a/add?q=${encodeURIComponent(term)}`);
  };

  // 'j' opens the first feed with new posts. Kept in a ref so the key handler
  // registers once and always sees the current top-of-list feed.
  const firstUnreadRef = useRef<string | null>(null);
  firstUnreadRef.current = unreadFeeds[0]?.id ?? null;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Anonymous users cap at MAX_ANON_FEEDS feeds. Once they hit the limit, surface a card prompting
  // sign-up for more. Dismissable for the session; reappears next visit while still at the limit.
  // Takes priority over the keyboard card (only one card shows at a time).
  const atFeedQuota = anon && feeds.length >= MAX_ANON_FEEDS;
  const [quotaCardClosed, setQuotaCardClosed] = useState(false);
  const showQuotaCard = atFeedQuota && !quotaCardClosed;

  // One-time "keyboard user" tips card: shown to people who have navigated posts with
  // j/k/n/p (a per-device localStorage signal set in Main.tsx), until they dismiss it -
  // the dismissal is stored in the synced `dismissed` preference so it stays gone everywhere.
  // Never in the native app: it's a phone app, and a hardware keyboard there is the rare exception.
  const usedReaderKeys = useMemo(() => {
    if (isNativeApp) return false;
    try { return localStorage.getItem(READER_KEYS_USED_KEY) == '1'; } catch { return false; }
  }, []);
  const [kbdCardClosed, setKbdCardClosed] = useState(false);
  const showKbdCard = usedReaderKeys && !preferences.dismissed?.includes(KBD_CARD_DISMISS) && !kbdCardClosed && !showQuotaCard;
  const dismissKbdCard = () => {
    setKbdCardClosed(true);
    if (!preferences.dismissed?.includes(KBD_CARD_DISMISS))
      updatePreferences({ dismissed: (preferences.dismissed || '') + KBD_CARD_DISMISS });
  };
  const showKbdCardRef = useRef(showKbdCard);
  showKbdCardRef.current = showKbdCard;
  const dismissKbdCardRef = useRef(dismissKbdCard);
  dismissKbdCardRef.current = dismissKbdCard;

  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      const tag = el?.tagName.toLowerCase();
      return tag == 'input' || tag == 'textarea' || !!(el as HTMLElement | null)?.isContentEditable;
    };
    // Priority above the layout's Escape-collapses-sidebar handler so Esc closes the card
    // first when it's open; when it's not, we return false and Esc falls through as before.
    registerKeyHandler('home-page', {
      'j': () => {
        if (isTyping()) return false;
        const id = firstUnreadRef.current;
        if (!id) return false;
        navigateRef.current(`/a/feed/${id}`);
        return true;
      },
      'r': () => {
        if (isTyping()) return false;
        navigateRef.current('/a/read-later');
        return true;
      },
      ...Object.fromEntries(ADD_FEED_KEYS.map(k => [k, () => {
        if (isTyping()) return false;
        navigateRef.current('/a/add');
        return true;
      }])),
      'Escape': () => {
        if (!showKbdCardRef.current) return false;
        dismissKbdCardRef.current();
        return true;
      },
    }, false, 10);
    return () => unregisterKeyHandler('home-page');
  }, []);
  // Feeds/folders with unread posts that didn't fit in the shown cards, summarized
  // in a single line between the cards and the "All unread posts" card.
  const overflow = useMemo(() => {
    if (folderMode) {
      const rest = homeCards.slice(recentLimit);
      return {
        feedCount: rest.filter(c => c.kind == 'feed').length,
        folderCount: rest.filter(c => c.kind == 'folder').length,
        postCount: rest.reduce((s, c) => s + (c.kind == 'folder' ? c.unread : feedEngine.getUnreadCount(c.feed.id)), 0),
      };
    }
    const rest = unreadFeeds.slice(recentLimit);
    return { feedCount: rest.length, folderCount: 0, postCount: rest.reduce((s, f) => s + feedEngine.getUnreadCount(f.id), 0) };
  }, [folderMode, homeCards, unreadFeeds, feedEngine, recentLimit]);

  // 2D spatial navigation so arrow keys move across the multi-column grids
  // (option cards, "Jump back in", Discover tiles) row/column-wise.
  const optionCardKeyHandler = get2DKeyHandler((el: any) => el.click()) as any;

  // Two home modes, triggered by whether the user already has a real
  // library (>1 feed) - the same threshold the dashboard sections use.
  // Returning users get a slim welcome-back hero instead of the setup
  // flow (the dashboard below already gives them their actions).
  const returning = feeds.length > 1;
  // Reword the welcome hero when the user comes back to a big backlog.
  const bankruptcy = bankruptcyConditionsMet(feedEngine, preferences);

  // From the user's previous activity (captured at app start, before App stamps
  // "now" - see connector/lastActive): whether to greet a returning user with
  // "Welcome back", and whether an empty unread list should read as "nothing new
  // yet". Measured from real app usage, not just Home visits. 0 => treat as long ago.
  const [{ showWelcomeBack, postsLikelyStale }] = useState(() => {
    const prev = getLastActiveMs();
    const elapsed = prev ? Date.now() - prev : Infinity;
    return {
      // No prior-activity record means this is a first-ever load (e.g. a visitor who just signed up) — a brand-new user, not a returning one — so never greet
      // them with "Welcome back", even though `elapsed` reads as Infinity.
      showWelcomeBack: !!prev && elapsed >= WELCOME_BACK_AFTER_MS,
      postsLikelyStale: elapsed > CAUGHT_UP_STALE_MS,
    };
  });

  // Until the engine has actually loaded the feed list (OFF/LOADING) we
  // can't tell new from returning users. Render a spinner instead of
  // briefly flashing the new-user setup card (flicker on direct load).
  // ERROR is neither ready nor loading: the library exists but couldn't be
  // loaded, so the new-user welcome must NOT show — after a server error it
  // reads as "your feeds are gone". Render an error panel instead.
  const engineState = useEngineState(feedEngine);
  const engineFailed = engineState == 'ERROR';
  const engineReady = engineState != 'OFF' && engineState != 'LOADING' && !engineFailed;

  // Remember the scroll position when leaving; when returning through a "back to Home" button,
  // restore it or scroll to the requested Discover category. The page grows as its async sections
  // (Discover, stats) arrive, so keep retrying on re-renders until the target is reached, but give
  // up after a few seconds so a page that stays shorter never snaps the user back later.
  const pageRef = useRef<HTMLDivElement>(null);
  const [scrollTarget] = useState(takeHomeScrollTarget);
  const restoreUntilRef = useRef(scrollTarget ? Date.now() + 3000 : 0);
  useLayoutEffect(() => () => { if (pageRef.current) saveHomeScroll(pageRef.current.scrollTop); }, []);
  useLayoutEffect(() => {
    const el = pageRef.current;
    if (!el || !scrollTarget || !engineReady || Date.now() > restoreUntilRef.current) return;
    let top: number;
    if ('top' in scrollTarget)
      top = scrollTarget.top;
    else {
      const anchor = document.getElementById(homeCategoryAnchor(scrollTarget.category));
      if (!anchor) return;
      top = el.scrollTop + anchor.getBoundingClientRect().top - el.getBoundingClientRect().top - 16;
    }
    el.scrollTop = top;
    if (Math.abs(el.scrollTop - top) < 2) restoreUntilRef.current = 0;
  });

  const [heroClosed, setHeroClosed] = useState(false);
  const showHero = !heroClosed && (returning ? showWelcomeBack : !preferences.dismissed?.includes(HERO_DISMISS));
  const dismissHero = () => {
    setHeroClosed(true);
    if (!returning && !preferences.dismissed?.includes(HERO_DISMISS))
      updatePreferences({ dismissed: (preferences.dismissed || '') + HERO_DISMISS });
  };

  // Empty unread list right after the user finished reading: read fresh (not captured at
  // load) so it reflects a mark-read that happened during this session.
  const justCaughtUp = counts.unread == 0 && Date.now() - getLastMarkReadMs() < JUST_CAUGHT_UP_MS;

  // A section title that collapses/expands its section (chevron points down while open). A
  // collapsed section can summarize itself with a count badge.
  const renderSectionTitle = (section: HomeSection, title: string, className = 'homeSectionTitle', collapsedBadge = 0) => {
    const open = !collapsedSections.has(section);
    return (
      <h2 className={className}>
        <button
          type="button"
          tabIndex={0}
          className="homeSectionToggle flowFocussable flow2d"
          aria-expanded={open}
          onClick={() => toggleSection(section)}
          onKeyDown={optionCardKeyHandler}
        >
          {title}
          <span className={`homeSectionChevron${open ? ' open' : ''}`}><ChevronRightIcon size={16} /></span>
          {!open && collapsedBadge > 0 && <span className="homeRecentBadge homeSectionBadge">{collapsedBadge > 999 ? '999+' : collapsedBadge}</span>}
        </button>
      </h2>
    );
  };

  // One "New posts available" card for a feed — used by the feed view and, for
  // unfiled feeds, by the folder view.
  const renderFeedCard = (f: RestUserFeed) => {
    const unread = feedEngine.getUnreadCount(f.id);
    const browseOnly = feedEngine.getShowRead(f.id) == 'a';
    const isNewsletter = f.feedStatus == 'NEWSLETTER';
    const meta = formatLastPostTime(f.lastNewPostPubDate) || formatCategory(f.category) || '';
    return (
      <button
        key={f.id}
        type="button"
        tabIndex={0}
        className="homeRecentCard flowFocussable flow2d"
        onClick={() => navigate(`/a/feed/${f.id}`)}
        onKeyDown={optionCardKeyHandler}
      >
        <FeedIcon className="homeRecentIcon" src={getFeedIconUrl(f, 48)} size={22} circular={f.circularIcon} fallback={<span className="homeRecentIcon homeRecentIconFallback">📰</span>} />
        <span className="homeRecentText">
          <span className="homeRecentName">{f.shortTitle || f.title}</span>
          {isNewsletter
            ? <span className="homeRecentMeta homeRecentMetaNewsletter"><MailIcon size={12} color="currentColor" />Newsletter Inbox</span>
            : meta && <span className="homeRecentMeta">{meta}</span>}
        </span>
        {unread > 0 && (browseOnly
          ? <span className="homeRecentDot" />
          : <span className="homeRecentBadge">{unread > 99 ? '99+' : unread}</span>)}
      </button>
    );
  };

  // A folder card: colored folder icon and member summary. Clicking opens the folder's
  // unread view — or the feed itself when only one member feed has unread posts.
  const renderFolderCard = (c: Extract<HomeCard, { kind: 'folder' }>) => {
    // The badge counts only tracked ('u') feeds. When untracked ('a') feeds in the folder also have
    // unread posts, append '+' to signal "and more untracked posts" (e.g. '8+'); a folder whose only
    // unread posts are untracked shows a bare dot instead of a number.
    const trackedUnread = c.members.reduce((s, f) => s + (feedEngine.getShowRead(f.id) == 'u' ? feedEngine.getUnreadCount(f.id) : 0), 0);
    const hasUntrackedUnread = c.members.some(f => feedEngine.getShowRead(f.id) == 'a' && feedEngine.getUnreadCount(f.id) > 0);
    return (
      <button
        key={`folder:${c.name}`}
        type="button"
        tabIndex={0}
        className="homeRecentCard flowFocussable flow2d"
        onClick={() => navigate(c.members.length == 1 ? `/a/feed/${c.members[0].id}` : `/a/new/${encodeURIComponent(c.name)}`)}
        onKeyDown={optionCardKeyHandler}
      >
        <span className="homeRecentIcon homeFolderIcon" style={{ color: nameColor(c.name) }}><FolderIcon size={22} /></span>
        <span className="homeRecentText">
          <span className="homeRecentName">{c.name}</span>
          <span className="homeRecentMeta">{c.members.length} {c.members.length == 1 ? 'feed' : 'feeds'} with new posts</span>
        </span>
        {trackedUnread > 0
          ? <span className="homeRecentBadge">{trackedUnread > 99 ? '99+' : `${trackedUnread}${hasUntrackedUnread ? '+' : ''}`}</span>
          : hasUntrackedUnread ? <span className="homeRecentDot" /> : null}
      </button>
    );
  };

  return (
    <ShortcutsContext.Provider value={HOME_SHORTCUTS}>
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
      <MainLayout
        selectedFeed="home"
        right={({ onExpand }) => (
          <div className="homePage" ref={pageRef}>
            <PageHeader
              title={!engineReady ? '' : returning ? 'Home' : (
                <>
                  <span className="homeTitleFull">Welcome to Downread</span>
                  <span className="homeTitleShort">Welcome</span>
                </>
              )}
              onMenu={onExpand}
              rightContent={
                <HeaderTools
                  onSearch={toggleSearch}
                  searchActive={searchState == 'open'}
                  searchRef={searchButtonRef}
                />
              }
            />
            {readingOffline && (
              <OfflineNotice>
                Downread shows the feeds and posts stored on this device for offline reading. What you
                read, star or save for later is kept and sent as soon as you're back online.
              </OfflineNotice>
            )}
            <FlowForm main={true}>
              <div className="homeContent">
                {searchState != 'closed' && (
                  <div
                    className={`homeSearchPanel${searchState == 'closing' ? ' closing' : ''}`}
                    onAnimationEnd={e => { if (e.target == e.currentTarget && searchState == 'closing') setSearchState('closed'); }}
                  >
                    <div className="homeSearchClip">
                      <div className="homeSearchBar">
                        <FeedSearchBox
                          value={searchInput}
                          onChange={setSearchInput}
                          onSubmit={submitSearch}
                          onSelectCategory={cat => openTopics(`/a/add/${cat}`)}
                          onSelectFeed={id => { setFeedOpenedFromSearch(id); navigate(`/a/feed/${id}`); }}
                          categories={searchCategories}
                          onEscape={closeSearch}
                          autoFocus
                        />
                      </div>
                    </div>
                  </div>
                )}
                {engineFailed ? (
                  <FeedLoadError />
                ) : !engineReady ? (
                  <div className="homeLoading"><Spinner spinning={true} /></div>
                ) : (<>
                {showHero && (
                  <div className="homeHero">
                    <button
                      type="button"
                      className="homeHeroClose flowFocussable sidewayFocus"
                      tabIndex={0}
                      aria-label="Dismiss"
                      onClick={dismissHero}
                      onKeyDown={getKeyHandler(dismissHero)}
                    >&times;</button>
                    {returning ? (
                      <>
                        <h1 className="homeHeroTitle">Welcome back.</h1>
                        <p className="homeHeroSubtitle">{bankruptcy
                          ? 'A lot of posts have piled up since you last visited.'
                          : 'Pick up where you left off, or explore something new.'}</p>
                      </>
                    ) : fromShare ? (
                      <>
                        <h1 className="homeHeroTitle">You're reading on Downread.</h1>
                        <p className="homeHeroSubtitle">
                          Downread is a free RSS reader: follow any blog, news site, or YouTube channel
                          and read every new post in one fast, uncluttered stream. Add more feeds below.<br/>
                          You're using it without an account, so your feeds live only in this browser
                          (they don't sync to other devices, and clearing your browser data removes them).
                          You can follow up to {MAX_ANON_FEEDS} feeds. <a href="/signup">Sign up free</a> any
                          time to keep them safe, follow more feeds and read them everywhere.
                        </p>
                      </>
                    ) : (
                      <>
                        <h1 className="homeHeroTitle">{anon ? 'Find something worth reading.' : "Let's find your first feeds."}</h1>
                        <p className="homeHeroSubtitle">
                          {anon
                            ? "Downread is a feed reader: follow the blogs, news sites, and YouTube channels you love, and keep up with everything they post."
                            : 'Downread brings the sites you love together in one place. Browse the feeds below and follow the ones that catch your eye.'}
                        </p>
                      </>
                    )}
                  </div>
                )}

                {returning && !showQuotaCard && <RssBankruptcyCard context="home" />}

                {showQuotaCard && <FeedQuotaCard onClose={() => setQuotaCardClosed(true)} />}

                {showKbdCard && (
                  <div className="homeKbdCard">
                    <button
                      type="button"
                      className="homeKbdClose flowFocussable sidewayFocus"
                      tabIndex={0}
                      aria-label="Dismiss"
                      onClick={dismissKbdCard}
                      onKeyDown={getKeyHandler(dismissKbdCard)}
                    >&times;</button>
                    <div className="homeKbdHeader">
                      <span className="homeKbdIcon"><KeyboardIcon size={26} /></span>
                      <h2 className="homeKbdTitle">Hello, fellow keyboard user!</h2>
                    </div>
                    <ul className="homeKbdList">
                      <li>
                        Press <kbd className="homeKbd">?</kbd> on any page to see every
                        available keyboard shortcut.
                      </li>
                      <li>
                        Navigate through the whole app with the arrow keys and <kbd className="homeKbd">Return</kbd>.
                        Press <kbd className="homeKbd">↓</kbd> to get going.
                      </li>
                    </ul>
                    <div className="homeKbdFooter">
                      <FocusText className="homeKbdDismiss" focussable="flow" underline onClick={dismissKbdCard}>
                        Got it, don't show again
                      </FocusText>
                      <kbd className="homeKbd">Esc</kbd>
                    </div>
                  </div>
                )}

                {/* Belongs to the welcome hero: closing that removes it too. */}
                {!returning && anon && showHero && (
                  <div className="homeAnonFooter">
                    <p className="homeAnonNote">
                      New to Downread?{' '}
                      <FocusText className="homeAnonLink" focussable="flow2d" underline onClick={() => goTo(routerCtx, '/a/help/intro')}>
                        See how it works
                      </FocusText>.
                    </p>
                  </div>
                )}

                {feeds.length > 1 && counts.unread > 0 && (
                  <div className="homeDashboard">
                    <div className="homeRecent">
                      <div className="homeSectionHeader">
                        {renderSectionTitle('new', 'New posts available', undefined, counts.unread)}
                        {folderViewAvailable && !collapsedSections.has('new') && (
                          <FocusText
                            className="homeGroupToggle"
                            focussable="flow2d"
                            onClick={() => updatePreferences({ homeGrouping: folderMode ? 'feeds' : 'folders' })}
                          >
                            {folderMode ? 'Show feeds' : 'Show folders'}
                          </FocusText>
                        )}
                      </div>
                      {!collapsedSections.has('new') && <div className="homeRecentGrid" ref={recentGridRef}>
                        {folderMode
                          ? recentCards.map(c => c.kind == 'folder' ? renderFolderCard(c) : renderFeedCard(c.feed))
                          : recentFeeds.map(renderFeedCard)}
                        {(overflow.feedCount > 0 || overflow.folderCount > 0) && (
                          <p className="homeRecentMore">
                            … and {overflow.postCount} {overflow.postCount == 1 ? 'post' : 'posts'} in {[
                              overflow.folderCount ? `${overflow.folderCount} other ${overflow.folderCount == 1 ? 'folder' : 'folders'}` : '',
                              overflow.feedCount ? `${overflow.feedCount} other ${overflow.feedCount == 1 ? 'feed' : 'feeds'}` : '',
                            ].filter(Boolean).join(' and ')}.
                          </p>
                        )}
                        {recentFeeds.length > 1 && (
                          <button type="button" tabIndex={0} className="homeRecentCard homeSpecialCard homeRecentCardFull flowFocussable flow2d" onClick={() => navigate('/a/new')} onKeyDown={optionCardKeyHandler}>
                            <span className="homeRecentIcon homeSpecialIcon"><NewPostsIcon size={20} /></span>
                            <span className="homeRecentText">
                              <span className="homeRecentName">All unread posts</span>
                              <span className="homeRecentMeta">Everything new across your feeds</span>
                            </span>
                            {/* Like the sidebar's "Unread posts": tracked ('u') feeds only, '+' when untracked
                                ('a') feeds add unread posts, a bare dot when only untracked feeds have any. */}
                            {counts.trackedUnread
                              ? <span className="homeRecentBadge">{counts.trackedUnread > 999 ? '999+' : `${counts.trackedUnread}${counts.untrackedUnread ? '+' : ''}`}</span>
                              : counts.untrackedUnread && <span className="homeRecentDot" />}
                          </button>
                        )}
                      </div>}
                    </div>
                  </div>
                )}

                {feeds.length > 1 && counts.unread == 0 && (
                  <div className="homeDashboard">
                    <div className="homeCaughtUp">
                      <AllReadIllustration size={170} />
                      <h2 className="homeCaughtUpHeadline">
                        {justCaughtUp ? 'All read.' : postsLikelyStale ? 'No new posts yet' : "You're all caught up!"}
                      </h2>
                      <p className="homeCaughtUpText">
                        {justCaughtUp
                          ? 'Check back later, or explore something new in the meantime.'
                          : postsLikelyStale
                          ? 'Nothing new has arrived in your feeds since your last visit. Check back later, or explore something new in the meantime.'
                          : 'There are no unread posts in any of your feeds right now. Check back later, or explore new feeds in the meantime.'}
                      </p>
                      {counts.readLater > 0 && (
                        <button
                          type="button"
                          tabIndex={0}
                          className="homeCaughtUpLink flowFocussable flow2d"
                          onClick={() => navigate('/a/read-later')}
                          onKeyDown={optionCardKeyHandler}
                        >
                          {counts.readLater} saved in Read Later →
                        </button>
                      )}
                    </div>
                    <div className="homePromoGrid">
                      <button
                        type="button"
                        tabIndex={0}
                        className="homeOptionCard flowFocussable flow2d"
                        onClick={() => navigate('/a/add')}
                        onKeyDown={optionCardKeyHandler}
                      >
                        <span className="homeOptionIcon"><PlusIcon size={26} /></span>
                        <span className="homeOptionText">
                          <span className="homeOptionTitle">Find more feeds</span>
                          <span className="homeOptionDesc">Search for any site, or paste a feed URL.</span>
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Discover, Categories and stats come from the server and lead to feeds you can't add offline. */}
                {!readingOffline && !!homeContent.data?.categories.length && (
                  <div className="homeDiscover">
                    <div className="homeSectionHeader">{renderSectionTitle('discover', 'Discover')}</div>
                    {!collapsedSections.has('discover') && homeContent.data.categories.map(category => (
                      <div key={category.id} id={homeCategoryAnchor(category.slug)} className="homeDiscoverCategory">
                        <div className="homeDiscoverHeader">
                          <h3 className="homeDiscoverTitle">{category.title}</h3>
                          <button
                            type="button"
                            tabIndex={0}
                            className="homeSeeAll flowFocussable flow2d"
                            onClick={() => navigate(`/a/home/${category.slug}`)}
                            onKeyDown={optionCardKeyHandler}
                          >
                            See all<ChevronRightIcon size={12} />
                          </button>
                        </div>
                        <div className="homeDiscoverRow discoverTiles">
                          {category.feeds.map(f => <DiscoverTile key={f.id} feed={f} onOpen={() => { setPreviewReturnPath(undefined); setFeedOpenedFromTile(f.id); navigate(`/a/feed/${f.id}`); }} />)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* While collapsed the topics aren't fetched, but the title must stay to expand it again. */}
                {!readingOffline && (collapsedSections.has('categories') || !!topics.length) && (
                  <div className="homeCategories">
                    <div className="homeSectionHeader">
                      {renderSectionTitle('categories', 'Categories')}
                      {!collapsedSections.has('categories') && (
                        <button
                          type="button"
                          tabIndex={0}
                          className="homeSeeAll homeCategoriesSeeAll flowFocussable flow2d"
                          onClick={() => openTopics('/a/add/topics')}
                          onKeyDown={optionCardKeyHandler}
                        >
                          See all {topics.length}<ChevronRightIcon size={12} />
                        </button>
                      )}
                    </div>
                    {!collapsedSections.has('categories') && (
                      <div className="addFeedTopicGrid" ref={topicGridRef}>
                        {topics.slice(0, 2 * topicCols).map(cat => (
                          <CategoryTile key={cat} category={cat} banner onCategoryClick={cat => openTopics(`/a/add/${cat}`)} />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* stats.feeds is never legitimately 0 - a published file that says so came from a
                    server without the public feeds, so hide the section rather than show "0 feeds". */}
                {!readingOffline && returning && !!onboarding.data?.stats?.feeds && (
                  <div className="homeGlobal">
                    {renderSectionTitle('stats', 'Downread stats')}
                    {!collapsedSections.has('stats') && <div className="homeGlobalStats">
                      <div className="homeGlobalStat">
                        <span className="homeGlobalNum">{formatStatCount(onboarding.data.stats.feeds, 100)}</span>
                        <span className="homeGlobalLabel">feeds</span>
                      </div>
                      <div className="homeGlobalStat">
                        {/* Narrow columns get the abbreviated forms ("3.3M", "new/hour") - see
                            the container query in HomePage.css. */}
                        <span className="homeGlobalNum">
                          <span className="homeStatFull">{formatStatCount(onboarding.data.stats.posts, 1000)}</span>
                          <span className="homeStatShort">{formatStatCount(onboarding.data.stats.posts, 1000, true)}</span>
                        </span>
                        <span className="homeGlobalLabel">posts</span>
                      </div>
                      <div className="homeGlobalStat">
                        <span className="homeGlobalNum">{onboarding.data.stats.postsLastHour.toLocaleString()}</span>
                        <span className="homeGlobalLabel">
                          <span className="homeStatFull">new this hour</span>
                          <span className="homeStatShort">new/hour</span>
                        </span>
                      </div>
                    </div>}
                  </div>
                )}

                <LegalFooter tight={!returning} />
                </>)}
              </div>
            </FlowForm>
          </div>
        )}
        startWithLeftCollapsed={typeof window !== 'undefined' && window.innerWidth <= 750}
      />
    </FlowFormLeaveContext.Provider>
    </ShortcutsContext.Provider>
  );
};

export default HomePage;
